import React, { useState, useEffect } from 'react';
import {
    Container, Box, Typography, Button, TextField, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, IconButton, CircularProgress, Switch, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Percent as PercentIcon,
    Edit as EditIcon,
    Check as CheckIcon,
    Close as CloseIcon,


} from '@mui/icons-material';
import { toast } from 'react-toastify';
import commissionService from '../services/Commissionservice';
import '../styles/ProtectedRoutes.css';

const GOLD = '#bc965e';

const AdminCommission = () => {
    const [commissions, setCommissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newPercentage, setNewPercentage] = useState('');
    const [adding, setAdding] = useState(false);
    const [updatingId, setUpdatingId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);

    useEffect(() => {
        loadCommissions();
    }, []);

    const loadCommissions = async () => {
        try {
            setLoading(true);
            const res = await commissionService.getAll();
            if (res.success) setCommissions(res.data);
        } catch (error) {
            toast.error(error.message || 'Failed to load commissions');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async () => {
        if (commissions.length > 0) {
            toast.error('Only one commission rate is allowed. Edit the existing one instead.');
            return;
        }

        const value = parseFloat(newPercentage);
        if (!newPercentage || isNaN(value) || value <= 0 || value > 100) {
            toast.error('Enter a valid percentage between 0 and 100');
            return;
        }

        setAdding(true);
        try {
            const res = await commissionService.add(value);
            if (res.success) {
                toast.success('Commission added successfully');
                setNewPercentage('');
                await loadCommissions();
            }
        } catch (error) {
            toast.error(error.message || 'Failed to add commission');
        } finally {
            setAdding(false);
        }
    };

    const handleStartEdit = (row) => {
        setEditingId(row.id);
        setEditValue(String(row.commission_percentage));
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditValue('');
    };

    const handleSaveEdit = async (row) => {
        const value = parseFloat(editValue);
        if (!editValue || isNaN(value) || value <= 0 || value > 100) {
            toast.error('Enter a valid percentage between 0 and 100');
            return;
        }
        if (value === row.commission_percentage) {
            handleCancelEdit();
            return;
        }

        setSavingEdit(true);
        try {
            const res = await commissionService.update(row.id, value, row.status);
            if (res.success) {
                toast.success('Commission updated');
                setEditingId(null);
                setEditValue('');
                await loadCommissions();
            }
        } catch (error) {
            toast.error(error.message || 'Failed to update commission');
        } finally {
            setSavingEdit(false);
        }
    };

    // Only one commission should be active at a time (creditReferral picks
    // WHERE status = 1 LIMIT 1). Activating a row deactivates the others.
    const handleToggleActive = async (row) => {
        const activating = row.status !== 1;
        setUpdatingId(row.id);
        try {
            if (activating) {
                const currentlyActive = commissions.filter(c => c.status === 1 && c.id !== row.id);
                for (const active of currentlyActive) {
                    await commissionService.update(active.id, active.commission_percentage, 0);
                }
                await commissionService.update(row.id, row.commission_percentage, 1);
                toast.success(`${row.commission_percentage}% is now the active commission rate`);
            } else {
                await commissionService.update(row.id, row.commission_percentage, 0);
                toast.info('Commission deactivated');
            }
            await loadCommissions();
        } catch (error) {
            toast.error(error.message || 'Failed to update commission');
        } finally {
            setUpdatingId(null);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        try {
            await commissionService.remove(deleteTarget.id);
            toast.success('Commission deleted');
            setDeleteTarget(null);
            await loadCommissions();
        } catch (error) {
            toast.error(error.message || 'Failed to delete commission');
        }
    };

    return (
        <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 2, md: 3 } }}>

            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" className="glass-title" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Referral Commission
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    Set the percentage credited to a customer's wallet when someone uses their referral link to place an order.
                    Only one rate can be active at a time.
                </Typography>
            </Box>

            {/* Add new commission */}
            {/* Add new commission - only allowed when none exists yet */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 1.5,
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid rgba(188,150,94,0.25)',
                    background: 'rgba(188,150,94,0.04)',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    opacity: commissions.length > 0 ? 0.5 : 1,
                }}
            >
                <TextField
                    size="small"
                    label="Commission %"
                    type="number"
                    value={newPercentage}
                    onChange={(e) => setNewPercentage(e.target.value)}
                    disabled={commissions.length > 0}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    sx={{
                        minWidth: 160,
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: 'rgba(188,150,94,0.4)' },
                            '&:hover fieldset': { borderColor: GOLD },
                        },
                        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
                        '& .MuiOutlinedInput-input': { color: '#fff' },
                    }}
                />
                <Button
                    variant="contained"
                    startIcon={adding ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    onClick={handleAdd}
                    disabled={adding || commissions.length > 0}
                    sx={{
                        background: '#c49659 !important',
                        color: '#000',
                        fontWeight: 700,
                        '&:hover': {
                            background: '#c49659 !important',
                        },
                        '&.Mui-disabled': {
                            background: 'rgba(152, 96, 21, 0.87) !important',
                            color: 'rgba(250, 250, 250, 0.4)',
                        },
                    }}
                >
                    Add Commission
                </Button>
                {commissions.length > 0 && (
                    <Typography variant="caption" sx={{ color: '#ffffff', width: '100%' }}>
                        A commission rate already exists. Edit the existing rate percertage.
                    </Typography>
                )}
            </Box>

            {/* Commission list */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress sx={{ color: GOLD }} />
                </Box>
            ) : commissions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <PercentIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.2)', mb: 1 }} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        No commission rates configured yet
                    </Typography>
                </Box>
            ) : (
                <TableContainer
                    component={Paper}
                    sx={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(188, 150, 94, 0.58)' }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>Percentage</TableCell>
                                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>Updated</TableCell>
                                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {commissions.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>
                                        {editingId === row.id ? (
                                            <TextField
                                                size="small"
                                                type="number"
                                                autoFocus
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleSaveEdit(row);
                                                    if (e.key === 'Escape') handleCancelEdit();
                                                }}
                                                inputProps={{ min: 0, max: 100, step: 0.1 }}
                                                sx={{
                                                    width: 100,
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: 'rgba(188,150,94,0.4)' },
                                                        '&:hover fieldset': { borderColor: GOLD },
                                                    },
                                                    '& .MuiOutlinedInput-input': { color: '#fff', py: 0.6 },
                                                }}
                                            />
                                        ) : (
                                            `${row.commission_percentage}%`
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Tooltip title={row.status === 1 ? 'Active — click to deactivate' : 'Inactive — click to activate'}>
                                                <span>
                                                    <Switch
                                                        checked={row.status === 1}
                                                        onChange={() => handleToggleActive(row)}
                                                        disabled={updatingId === row.id}
                                                        size="small"
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': { color: GOLD },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: GOLD },
                                                        }}
                                                    />
                                                </span>
                                            </Tooltip>
                                            <Chip
                                                label={row.status === 1 ? 'Active' : 'Inactive'}
                                                size="small"
                                                sx={{
                                                    bgcolor: row.status === 1 ? 'rgba(76,175,80,0.15)' : 'rgba(255,255,255,0.08)',
                                                    color: row.status === 1 ? '#4caf50' : 'rgba(255,255,255,0.5)',
                                                    fontWeight: 600,
                                                }}
                                            />
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.94)', fontSize: '0.8rem' }}>
                                        {row.updated_at ? new Date(row.updated_at).toLocaleDateString('en-IN') : '—'}
                                    </TableCell>

                                    <TableCell align="right">
                                        {editingId === row.id ? (
                                            <>
                                                <Tooltip title="Save">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleSaveEdit(row)}
                                                            disabled={savingEdit}
                                                            sx={{ color: '#4caf50' }}
                                                        >
                                                            {savingEdit ? <CircularProgress size={16} color="inherit" /> : <CheckIcon fontSize="small" />}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Cancel">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleCancelEdit}
                                                            disabled={savingEdit}
                                                            sx={{ color: 'rgba(255,255,255,0.5)' }}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </>
                                        ) : (
                                            <>
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleStartEdit(row)}
                                                        sx={{ color: GOLD }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setDeleteTarget(row)}
                                                        sx={{ color: '#f44336' }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                    </TableCell>

                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Delete confirmation */}
            <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
                <DialogTitle>Delete Commission Rate</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Delete the {deleteTarget?.commission_percentage}% commission rate? This cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AdminCommission;